import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { useDocumentTitle, useMe } from '../lib/hooks';
import { TINTS } from '../lib/format';
import { Avatar, Button, Field, Loading, Sheet } from '../components/ui';
import { useToast } from '../components/feedback';

export function TenantPicker() {
  useDocumentTitle('Plataformas');
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [nome, setNome] = useState('');
  const [busy, setBusy] = useState(false);

  if (!me.data) return <div className="page narrow"><Loading rows={3} height={74} /></div>;
  const { user, tenants, hasShares } = me.data;
  const ownsOne = tenants.some((t) => t.isOwner);

  const logout = async () => {
    await api.post('/api/auth/logout').catch(() => undefined);
    qc.clear();
    nav('/entrar', { replace: true });
  };

  return (
    <div style={{ minHeight: '100vh', padding: '34px 18px 60px' }}>
      <div className="anim-up" style={{ maxWidth: 620, margin: '0 auto' }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Olá, {user.nome.split(' ')[0]}</div>
        <h1 className="title-xl" style={{ margin: '0 0 6px' }}>Em qual plataforma você quer entrar?</h1>
        <div className="sub" style={{ fontSize: 14, marginBottom: 24 }}>
          {tenants.length > 1 ? 'Você tem a sua própria plataforma e participa das plataformas de outras nutricionistas.' : tenants.length === 1 ? 'Escolha onde trabalhar agora.' : 'Você ainda não participa de nenhuma plataforma.'}
        </div>
        <div className="stack gap-10">
          {tenants.map((t, i) => (
            <Link key={t.id} to={`/t/${t.id}`} className="item" style={{ padding: 16, borderRadius: 13, gap: 14 }}>
              <Avatar name={t.nome} size={42} round={false} color={TINTS[i % TINTS.length]} />
              <div className="flex1">
                <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.01em', marginBottom: 3 }}>{t.nome}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{t.meta}</div>
              </div>
              <span className="pill role" style={{ background: t.isOwner ? 'var(--accent-soft)' : 'var(--chip)', color: t.isOwner ? 'var(--resolved)' : 'var(--text-3)' }}>{t.roleLabel}</span>
            </Link>
          ))}
          {hasShares && (
            <Link to="/portal" className="item" style={{ padding: 16, borderRadius: 13, gap: 14 }}>
              <Avatar name="Compartilhados" size={42} round={false} color="#5F7386" />
              <div className="flex1">
                <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-.01em', marginBottom: 3 }}>Compartilhados comigo</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>Relatórios e restaurantes que outras pessoas compartilharam</div>
              </div>
              <span className="pill role" style={{ background: 'var(--chip)', color: 'var(--text-3)' }}>Portal</span>
            </Link>
          )}
          {!ownsOne && (
            <button className="empty" style={{ cursor: 'pointer', width: '100%' }} onClick={() => setCreating(true)}>
              <strong style={{ color: 'var(--accent-dark)' }}>+ Criar minha plataforma</strong>
              <div style={{ marginTop: 4 }}>Para registrar suas próprias visitas e convidar parceiras.</div>
            </button>
          )}
        </div>
        <button className="link-btn u muted" style={{ marginTop: 22, fontSize: 13 }} onClick={logout}>Sair da conta</button>
      </div>

      <Sheet open={creating} onClose={() => setCreating(false)} title="Criar minha plataforma">
        <Field label="Nome da plataforma"><input className="input" placeholder={`${user.nome} Consultoria`} value={nome} onChange={(e) => setNome(e.target.value)} /></Field>
        <Button
          variant="dark" size="lg" block loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const r = await api.post<{ id: string }>('/api/tenants', { nome: nome.trim() || `${user.nome} Consultoria` });
              await qc.invalidateQueries({ queryKey: ['me'] });
              nav(`/t/${r.id}`);
            } catch (e) {
              toast(errMsg(e), 'err');
            } finally {
              setBusy(false);
            }
          }}
        >
          Criar plataforma
        </Button>
      </Sheet>
    </div>
  );
}
